import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Search, Building2, ArrowLeft, ArrowRight } from "lucide-react";

export default function PortalFinderPage() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setSearched(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/portal-search", { params: { q: term } });
        setResults(data);
      } catch { setResults([]); }
      finally { setLoading(false); setSearched(true); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6" data-testid="portal-finder-page">
      <div className="w-full max-w-md">
        <div className="rounded-sm border border-border bg-card p-8 sm:p-10">
          <div className="font-heading text-xl font-semibold">Suivio<span className="text-primary">360</span></div>
          <h1 className="mt-6 text-2xl font-semibold tracking-tight">Trouver votre portail</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Saisissez le nom de l'entreprise qui gère vos demandes pour accéder à votre espace client.
          </p>

          <div className="mt-8 space-y-2">
            <Label htmlFor="portal-search">Nom de l'entreprise</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="portal-search" data-testid="portal-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Ex : Agua Propreté" className="rounded-sm pl-9" autoFocus />
            </div>
          </div>

          <div className="mt-4 space-y-2" data-testid="portal-search-results">
            {loading && <p className="text-sm text-muted-foreground">Recherche…</p>}
            {!loading && results.map((c) => (
              <Link key={c.slug} to={`/${c.slug}`} data-testid={`portal-result-${c.slug}`}
                className="flex items-center justify-between rounded-sm border border-border px-4 py-3 transition-colors hover:border-primary hover:bg-accent">
                <span className="flex items-center gap-2 font-medium">
                  <Building2 className="h-4 w-4 text-primary" /> {c.name}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
            {!loading && searched && results.length === 0 && (
              <p data-testid="portal-search-empty" className="rounded-sm border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">
                Aucune entreprise trouvée. Vérifiez l'orthographe ou utilisez le lien fourni par votre administrateur.
              </p>
            )}
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-1 text-primary hover:underline">
            <ArrowLeft className="h-3 w-3" /> Retour à la connexion
          </Link>
        </p>
      </div>
    </div>
  );
}
